import { useEffect, useState } from 'react';
import { createRoute, stripSearchParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { rootRoute, useAppRouterInitial } from '../routerBase';
import { getContact, contactsQueryKeys } from '../../api/contacts';
import {
  CONTACTS_SEARCH_DEFAULTS,
  parseContactsSearch,
} from '../../hooks/useContactRouterSearch';
import { TableViewSkeleton } from '../../components/Skeleton/TableViewSkeleton';
import { DetailsSkeleton } from '../../components/Skeleton/DetailsSkeleton';
import { ContactEditSkeleton } from '../../components/Skeleton/ContactEditSkeleton';
import { useTranslation } from '../../hooks/useTranslation';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
// Type-only imports so the per-route bundles stay split — the runtime modules
// are loaded via dynamic import() inside each route's effect (see below).
import type ContactManagement from '../../components/ContactManagement/index';
import type ContactDetails from '../../components/ContactDetails/index';
import type EditContact from '../../components/EditContact/index';
import type { ApiContact } from '../../api/contacts';

export const CONTACT_STALE_TIME_MS = 30_000;

// Shared query for /contacts/$id/details. Mirrors `useProductRaw`: the
// consumer hook `useContact` applies a `select` mapper, but the route
// component feeds the raw `ApiContact` into ContactDetails which seeds its
// own `useContact` query via the same `contactsQueryKeys.detail` cache key.
export function useContactRaw(idNum: number, seed: ApiContact | undefined) {
  return useQuery({
    queryKey: contactsQueryKeys.detail(idNum),
    queryFn: () => getContact(idNum),
    staleTime: CONTACT_STALE_TIME_MS,
    select: (data) => data.contact,
    ...(seed
      ? { initialData: { contact: seed }, initialDataUpdatedAt: Date.now() }
      : {}),
  });
}

export const contactsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contacts',
  validateSearch: parseContactsSearch,
  search: { middlewares: [stripSearchParams(CONTACTS_SEARCH_DEFAULTS)] },
  component: function ContactsIndexRoute() {
    const { initialContacts, currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    // First render renders the skeleton so React hydration byte-matches the
    // Phlex skeleton; the real component is dynamic-imported in an effect so
    // it stays in its own chunk.
    useDocumentTitle(t('common.page_titles.contacts'));
    const [Component, setComponent] = useState<typeof ContactManagement | null>(null);
    useEffect(() => {
      import('../../components/ContactManagement/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <TableViewSkeleton label={t('common.loading_contacts')} />;
    }
    return <Component initialData={initialContacts} currentUser={currentUser} />;
  },
});

export const contactDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contacts/$id/details',
  component: function ContactDetailsRoute() {
    const params = contactDetailsRoute.useParams();
    const { initialContact, currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed =
      initialContact && initialContact.id === idNum ? initialContact : undefined;
    const { data } = useContactRaw(idNum, seed);
    useDocumentTitle(data?.full_name ?? t('common.page_titles.contacts'));
    const [Component, setComponent] = useState<typeof ContactDetails | null>(null);
    useEffect(() => {
      import('../../components/ContactDetails/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component || !data) {
      return <DetailsSkeleton label={t('common.loading')} />;
    }
    return <Component contactId={idNum} initialContact={data} currentUser={currentUser} />;
  },
});

export const contactEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contacts/$id/edit',
  component: function ContactEditRoute() {
    const params = contactEditRoute.useParams();
    const { initialContact } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed =
      initialContact && initialContact.id === idNum ? initialContact : undefined;
    const { data } = useContactRaw(idNum, seed);
    useDocumentTitle(
      data ? t('common.page_titles.edit', { name: data.full_name }) : t('common.page_titles.contacts'),
    );
    const [Component, setComponent] = useState<typeof EditContact | null>(null);
    useEffect(() => {
      import('../../components/EditContact/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component || !data) {
      return <ContactEditSkeleton label={t('common.loading')} />;
    }
    return <Component contactId={idNum} initialContact={data} />;
  },
});
